import ReactMarkdown from "react-markdown";
import { renderToStaticMarkup } from "react-dom/server";
import remarkGfm from "remark-gfm";

/**
 * Strip legacy "thinking" / "article" wrappers that older AI generations
 * left in stored content. Also removes obvious AI preamble lines such as
 * "Let me analyze the task..." that some models still emit at the top.
 *
 * Safe to call on any article body — markdown or HTML, old or new.
 */
export function cleanArticleContent(raw: string): string {
  if (!raw) return "";

  let out = raw;

  // 1. Remove any <thinking>…</thinking> blocks (case-insensitive, multiline)
  out = out.replace(/<thinking\b[^>]*>[\s\S]*?<\/thinking\s*>/gi, "");
  // 2. Remove an unclosed <thinking> block at the tail (defensive)
  out = out.replace(/<thinking\b[^>]*>[\s\S]*$/gi, "");
  // 3. Drop the <article> wrapper tags entirely — they are not real HTML article
  out = out.replace(/<\/?article\b[^>]*>/gi, "");

  // 4. Strip a leading meta-commentary paragraph the model sometimes still emits.
  //    Example openings we want to skip:
  //      "Let me analyze the task before writing the article."
  //      "Sure! Here is the article:"
  //      "Below is the article:"
  //    We only strip the FIRST line if it matches one of these patterns and is
  //    clearly not part of the article body (i.e. is plain prose, not a heading
  //    or markdown URL line).
  const PREAMBLE_PATTERNS = [
    /^\s*(let me|i['’]?ll|i will|first[,]?|sure[!,]|here['’]?s|here is|below is|okay[,]?|alright[,]?)/i,
  ];
  const lines = out.split(/\r?\n/);
  while (lines.length) {
    const first = lines[0]?.trim() ?? "";
    if (!first) {
      lines.shift();
      continue;
    }
    // Stop stripping once we hit a heading / URL-meta / markdown structure
    if (
      first.startsWith("#") ||
      /^url[:\s]/i.test(first) ||
      /^page\s*title[:\s]/i.test(first) ||
      /^meta\s*(description|keywords)[:\s]/i.test(first) ||
      first.startsWith("- ") ||
      first.startsWith("* ")
    ) {
      break;
    }
    if (PREAMBLE_PATTERNS.some((re) => re.test(first))) {
      lines.shift();
      continue;
    }
    break;
  }
  out = lines.join("\n").trim();

  return out;
}

/**
 * Heuristic: does the cleaned content already contain rendered HTML
 * structure (h1/p/div…)? If yes, we should NOT pass it through Markdown
 * (would double-escape). Otherwise treat it as Markdown.
 */
export function looksLikeHtml(text: string): boolean {
  return /<(h[1-6]|p|div|strong|em|ul|ol|li|table|br)\b/i.test(text);
}

/**
 * Drop-in renderer for article content. Handles three input shapes:
 *   1. Cleaned Markdown  → rendered with react-markdown + GFM
 *   2. Pre-rendered HTML → injected via dangerouslySetInnerHTML
 *   3. Mixed legacy text → cleaned first, then routed to (1) or (2)
 *
 * Wrap the parent element in `prose prose-sm` (or larger) for typography.
 */
export function ArticleMarkdown({ content }: { content: string }) {
  const cleaned = cleanArticleContent(content);
  if (!cleaned) return null;

  if (looksLikeHtml(cleaned)) {
    return <div dangerouslySetInnerHTML={{ __html: cleaned }} />;
  }
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleaned}</ReactMarkdown>
  );
}

/* ───────────────────────────────────────────────────────────────
   Rich-Text Clipboard Support
   ─────────────────────────────────────────────────────────────
   Pasting raw Markdown (e.g. "# Title\n**Bold**") into Word /
   Google Docs / WordPress shows the literal hash+stars instead
   of rendered headings. To preserve visual formatting we copy
   BOTH a styled `text/html` payload AND a Markdown-stripped
   `text/plain` fallback in a single ClipboardItem.
   ─────────────────────────────────────────────────────────── */

/**
 * Render cleaned content into self-contained, inline-styled HTML
 * suitable for the system clipboard. Inline styles are required
 * because most rich-text targets (Word, Google Docs, WordPress
 * Gutenberg) discard external CSS and only honour element-level
 * `style` attributes plus semantic tags.
 */
export function articleToHtmlForCopy(raw: string): string {
  const cleaned = cleanArticleContent(raw);
  if (!cleaned) return "";

  const innerHtml = looksLikeHtml(cleaned)
    ? cleaned
    : renderToStaticMarkup(
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleaned}</ReactMarkdown>
      );

  return wrapWithInlineStyles(innerHtml);
}

/**
 * Plain-text fallback. Strips Markdown syntax (or HTML tags) so
 * targets that only accept text/plain still get a readable copy.
 */
export function articleToPlainText(raw: string): string {
  const cleaned = cleanArticleContent(raw);
  if (!cleaned) return "";

  if (looksLikeHtml(cleaned) && typeof document !== "undefined") {
    const tmp = document.createElement("div");
    tmp.innerHTML = cleaned;
    return (tmp.textContent ?? "").replace(/\n{3,}/g, "\n\n").trim();
  }

  return cleaned
    .replace(/^#{1,6}\s+/gm, "")              // ATX heading markers
    .replace(/\*\*(.+?)\*\*/g, "$1")          // bold
    .replace(/\*(.+?)\*/g, "$1")              // italic
    .replace(/`([^`]+)`/g, "$1")              // inline code
    .replace(/^\s*[-*]\s+/gm, "• ")           // bullet lists
    .replace(/^\s*\d+\.\s+/gm, (m) => m)      // keep ordered list numbers
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1 ($2)") // links
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1") // images → alt text
    .replace(/^>\s?/gm, "")                   // blockquote markers
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Inject element-level inline styles so heading sizes, bold,
 * lists and tables survive the trip into Word / Docs / CMS
 * editors. Keeps the source HTML semantic — we only decorate
 * existing tags rather than rewriting structure.
 */
function wrapWithInlineStyles(html: string): string {
  const styled = html
    .replace(/<h1(\s[^>]*)?>/gi,  '<h1$1 style="font-size:24pt;font-weight:700;margin:24px 0 12px;line-height:1.25;">')
    .replace(/<h2(\s[^>]*)?>/gi,  '<h2$1 style="font-size:18pt;font-weight:700;margin:20px 0 10px;line-height:1.3;">')
    .replace(/<h3(\s[^>]*)?>/gi,  '<h3$1 style="font-size:14pt;font-weight:700;margin:16px 0 8px;line-height:1.35;">')
    .replace(/<h4(\s[^>]*)?>/gi,  '<h4$1 style="font-size:12pt;font-weight:700;margin:12px 0 6px;">')
    .replace(/<p(\s[^>]*)?>/gi,   '<p$1 style="font-size:11pt;line-height:1.6;margin:0 0 10px;">')
    .replace(/<ul(\s[^>]*)?>/gi,  '<ul$1 style="margin:0 0 10px 1.5em;padding:0;">')
    .replace(/<ol(\s[^>]*)?>/gi,  '<ol$1 style="margin:0 0 10px 1.5em;padding:0;">')
    .replace(/<li(\s[^>]*)?>/gi,  '<li$1 style="margin:4px 0;line-height:1.55;">')
    .replace(/<blockquote(\s[^>]*)?>/gi,
      '<blockquote$1 style="border-left:4px solid #d1d5db;padding:0 1em;color:#4b5563;margin:0 0 10px;">')
    .replace(/<table(\s[^>]*)?>/gi,
      '<table$1 style="border-collapse:collapse;margin:10px 0;font-size:11pt;">')
    .replace(/<th(\s[^>]*)?>/gi,
      '<th$1 style="border:1px solid #9ca3af;padding:6px 10px;background:#f3f4f6;font-weight:700;text-align:left;">')
    .replace(/<td(\s[^>]*)?>/gi,
      '<td$1 style="border:1px solid #9ca3af;padding:6px 10px;">')
    .replace(/<code(\s[^>]*)?>/gi,
      '<code$1 style="font-family:Consolas,Menlo,monospace;background:#f3f4f6;padding:2px 4px;border-radius:3px;font-size:10.5pt;">')
    .replace(/<pre(\s[^>]*)?>/gi,
      '<pre$1 style="font-family:Consolas,Menlo,monospace;background:#f3f4f6;padding:10px 12px;border-radius:6px;white-space:pre-wrap;margin:0 0 10px;">')
    .replace(/<a(\s[^>]*)?>/gi,
      '<a$1 style="color:#2563eb;text-decoration:underline;">')
    .replace(/<strong(\s[^>]*)?>/gi, '<strong$1 style="font-weight:700;">')
    .replace(/<em(\s[^>]*)?>/gi,     '<em$1 style="font-style:italic;">');

  return `<div style="font-family:Arial,Helvetica,sans-serif;color:#1f2937;font-size:11pt;line-height:1.6;">${styled}</div>`;
}

/**
 * Copy an article (Markdown or HTML) to the clipboard while
 * preserving rich-text formatting.
 *
 *   1. Modern path → `navigator.clipboard.write([ClipboardItem])`
 *      with both `text/html` and `text/plain`. Works in Chrome,
 *      Edge, Safari 13.1+, Firefox 127+.
 *   2. Legacy path → render HTML into a hidden contentEditable
 *      `<div>`, select it, run `document.execCommand('copy')`.
 *      Covers older browsers and locked-down WebViews.
 *   3. Last-resort path → `writeText(plainText)` so the user at
 *      least gets the words, even if styling is dropped.
 *
 * Returns true on success, false if every path failed.
 */
export async function copyArticleToClipboard(raw: string): Promise<boolean> {
  const html  = articleToHtmlForCopy(raw);
  const plain = articleToPlainText(raw);

  if (!html && !plain) return false;

  // Path 1 — modern ClipboardItem API
  if (
    typeof window !== "undefined" &&
    typeof ClipboardItem !== "undefined" &&
    navigator.clipboard?.write
  ) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html":  new Blob([html],  { type: "text/html"  }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        }),
      ]);
      return true;
    } catch {
      // fall through to execCommand path
    }
  }

  // Path 2 — selection + execCommand("copy") on a hidden node
  if (typeof document !== "undefined" && document.execCommand) {
    try {
      const container = document.createElement("div");
      container.contentEditable = "true";
      container.innerHTML = html;
      container.style.position      = "fixed";
      container.style.left          = "-10000px";
      container.style.top           = "0";
      container.style.opacity       = "0";
      container.style.pointerEvents = "none";
      document.body.appendChild(container);

      const range = document.createRange();
      range.selectNodeContents(container);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);

      const ok = document.execCommand("copy");

      sel?.removeAllRanges();
      document.body.removeChild(container);

      if (ok) return true;
    } catch {
      // fall through to writeText
    }
  }

  // Path 3 — plain-text last resort
  try {
    await navigator.clipboard.writeText(plain);
    return true;
  } catch {
    return false;
  }
}
